/**
 * Role Constants for 3PLVision Dashboard
 * Maps user roles to numeric IDs and role names
 */

export const ROLE_TYPES = {
    ADMIN: 1,
    COMPANY_ADMIN: 1,
    SELLER: 2,
    SUPER_ADMIN: 3,
    WAREHOUSE_MANAGER: 4,
    WAREHOUSE_WORKER: 5,
    ONBOARDING_SELLER: 6
};

export const ROLE_NAMES = {
    1: 'Admin',
    2: 'Seller',
    3: 'Super Admin',
    4: 'Warehouse Manager',
    5: 'Warehouse Worker',
    6: 'Onboarding Seller'
};

const ROLE_NAME_TO_ID = {
    admin: ROLE_TYPES.ADMIN,
    seller: ROLE_TYPES.SELLER,
    'super admin': ROLE_TYPES.SUPER_ADMIN,
    'warehouse manager': ROLE_TYPES.WAREHOUSE_MANAGER,
    'warehouse worker': ROLE_TYPES.WAREHOUSE_WORKER,
    'onboarding seller': ROLE_TYPES.ONBOARDING_SELLER
};

const normalizeRoleName = (roleName) => String(roleName || '').trim().toLowerCase().replaceAll(/\s+/g, ' ');

export const resolveRoleId = (user, overrideRole) => {
    if (overrideRole !== null && overrideRole !== undefined && overrideRole !== '') {
        return Number(overrideRole) || 0;
    }

    const explicitRole =
        user?.roleId ??
        user?.RoleId ??
        user?.roleID ??
        user?.Role ??
        user?.role;

    if (!Number.isNaN(Number(explicitRole)) && Number(explicitRole) > 0) {
        return Number(explicitRole);
    }

    const roleName = normalizeRoleName(user?.roleName || user?.RoleName || user?.role);
    if (ROLE_NAME_TO_ID[roleName]) {
        return ROLE_NAME_TO_ID[roleName];
    }

    if (roleName.includes('super admin')) return ROLE_TYPES.SUPER_ADMIN;
    if (roleName.includes('warehouse worker')) return ROLE_TYPES.WAREHOUSE_WORKER;
    if (roleName.includes('warehouse manager')) return ROLE_TYPES.WAREHOUSE_MANAGER;
    if (roleName.includes('onboarding seller')) return ROLE_TYPES.ONBOARDING_SELLER;
    if (roleName.includes('seller')) return ROLE_TYPES.SELLER;
    if (roleName.includes('admin')) return ROLE_TYPES.ADMIN;
    if (roleName.includes('warehouse')) return ROLE_TYPES.WAREHOUSE_MANAGER;

    return ROLE_TYPES.SELLER;
};

export const canUseViewAs = (roleId) => Number(roleId) === ROLE_TYPES.SUPER_ADMIN;

export const isWarehouseRole = (roleId) =>
    [ROLE_TYPES.WAREHOUSE_MANAGER, ROLE_TYPES.WAREHOUSE_WORKER, ROLE_TYPES.SUPER_ADMIN].includes(Number(roleId));

/**
 * Widget categories for dashboard organization
 */
export const WIDGET_CATEGORIES = {
    SHIPMENTS: 'shipments',
    ORDERS: 'orders',
    INVENTORY: 'inventory',
    INBOUND: 'inbound',
    RETURNS: 'returns',
    CARRIER: 'carrier',
    OPERATIONS: 'operations',
    ADVANCED: 'advanced'
};

export const CATEGORY_LABELS = {
    shipments: 'Shipment & Label Analytics',
    orders: 'Order Analytics',
    inventory: 'Inventory & Product Insights',
    inbound: 'Inbound Analytics',
    returns: 'Returns & Refunds',
    carrier: 'Carrier Connect',
    operations: 'Operations & Tasks',
    advanced: 'Advanced Analytics & Performance'
};

/**
 * Phase definitions: which roles can see which widgets
 */
export const PHASE_ROLES = {
    PHASE_1: [ROLE_TYPES.SELLER, ROLE_TYPES.COMPANY_ADMIN, ROLE_TYPES.SUPER_ADMIN, ROLE_TYPES.ONBOARDING_SELLER],
    PHASE_2: [ROLE_TYPES.WAREHOUSE_MANAGER, ROLE_TYPES.WAREHOUSE_WORKER, ROLE_TYPES.COMPANY_ADMIN, ROLE_TYPES.SUPER_ADMIN],
    PHASE_3: [ROLE_TYPES.SUPER_ADMIN]
};
